/**
 * Plan Helpers - Frontend Utilities
 *
 * Client-side utilities for working with plan limits and add-ons.
 * Mirrors the structure from functions/config/planLimits.js (source of truth).
 */
import i18n from '../i18n';

// Plan limits configuration (mirrored from backend)
export const planLimits = {
  starter: {
    price: 147,
    botsIncluded: 1,
    botsMax: 2,
    seatsIncluded: 2,
    seatsMax: 2,
    numerosIncluded: 1,
    numerosMax: 1,
    // FASE 3g — agendas por ORGANIZACIÓN, ampliables con `addon_calendar` sin tope.
    // No es la cuota de asientos aunque el número coincida: un profesional que conecta
    // por link no es usuario de PUX. Ver `functions/config/planLimits.js`.
    agendasIncluded: 2,
    agendasMax: null,
    // Cuántas CUENTAS por canal, que es distinto de `channelsIncluded` —ése dice
    // QUÉ canales, no cuántas cuentas de cada uno—. Hasta el 7-sep-2026 no
    // existía ningún tope de cantidad: un Starter podía conectar tantas cuentas
    // de Instagram como quisiera y ninguna decisión de producto lo prohibía.
    //
    // Misma proporción que el resto del plan: 1 en Starter, 3 en Pro.
    cuentasPorCanalIncluded: 1,
    channelsIncluded: {
      whatsapp: true,
      instagram: true,
      tiktok: true,
      messenger: true,
      email: false
    },
    featuresIncluded: {
      calendar: true,
      ecommerce: true,
      repositorios: true,
      audioSTT: true,
      // `audioTTS: false` DESDE EL 8-oct-2026. El bot NUNCA ha enviado un audio: medido sobre
      // los 4.729 mensajes de la base, los salientes de tipo audio son CERO en todo el
      // histórico. La función existió —`synthesizeAudio`, con OpenAI TTS y gating de plan y de
      // minutos— y se retiró por muerta (`functions/index.js:381`): su único llamante era
      // `server/src/baileysSession.js:1089`, el servidor Baileys sin tráfico. El camino vivo
      // (Cloud API) nunca tuvo envío de audio, y en `functions/` no hay ninguno.
      //
      // Mientras estuvo en `true`, la landing prometía «respuestas por voz» y Facturación
      // pintaba «Audio TTS ✓» para algo que no existe. Volver a ponerlo en `true` exige
      // implementar el envío, no sólo el flag.
      audioTTS: false,
      vision: true
    },
    integrationSlots: {
      calendarOrEcommerce: 1
    },
    audioMinutesIncluded: 100,
    messagesIncluded: 24000,
    imagesIncluded: 200,
    reposIncludedMB: 250,
    procesamientoIncludedMB: 500,
    allowedAddons: [
      'addon_bot',
      'addon_repos',
      'addon_procesamiento',
      'addon_mensajes',
      'addon_ads_studio',
      'addon_calendar',
      'addon_ecommerce'
    ],
    addonLimits: {
      addon_bot: 1,
      addon_repos: null,
      addon_procesamiento: null,
      addon_mensajes: null,
      addon_ads_studio: 1,
      addon_calendar: null,
      addon_ecommerce: 1
    }
  },
  pro: {
    price: 297,
    botsIncluded: 3,
    botsMax: 5,
    seatsIncluded: 5,
    seatsMax: null,
    numerosIncluded: 3,
    numerosMax: 5,
    agendasIncluded: 5,
    agendasMax: null,
    // Cuántas CUENTAS por canal, que es distinto de `channelsIncluded` —ése dice
    // QUÉ canales, no cuántas cuentas de cada uno—. Hasta el 7-sep-2026 no
    // existía ningún tope de cantidad: un Starter podía conectar tantas cuentas
    // de Instagram como quisiera y ninguna decisión de producto lo prohibía.
    //
    // Misma proporción que el resto del plan: 1 en Starter, 3 en Pro.
    cuentasPorCanalIncluded: 3,
    channelsIncluded: {
      whatsapp: true,
      instagram: true,
      tiktok: true,
      messenger: true,
      // `email: false` HASTA LA ETAPA 2 DE CORREOS (8-oct-2026). Estaba en `true` y
      // prometía un canal que NO EXISTE: no hay ningún flujo de conexión en
      // `functions/integrations/`, y el mapa de nombres de canal
      // (`src/utils/ventasResumen.js`) sólo tiene los cuatro reales. Vuelve en la
      // etapa 2 de correos (correo como canal para todos los clientes vía Resend;
      // anotado en POST-LANZAMIENTO.md), y entonces se pone en `true` otra vez.
      //
      // Con esto NINGÚN canal distingue los dos planes: lo que los distingue es
      // `cuentasPorCanalIncluded` (1 contra 3). Lo fija
      // `__tests__/cuotaDeCuentasDeCanal.jest.test.js`.
      email: false
    },
    featuresIncluded: {
      calendar: true,
      ecommerce: true,
      repositorios: true,
      audioSTT: true,
      // `audioTTS: false` DESDE EL 8-oct-2026 (mismo motivo que en Starter). Medido sobre
      // los 4.729 mensajes de la base, los salientes de tipo audio son CERO en todo el
      // histórico. La función existió —`synthesizeAudio`, con OpenAI TTS y gating de plan y de
      // minutos— y se retiró por muerta (`functions/index.js:381`): su único llamante era
      // `server/src/baileysSession.js:1089`, el servidor Baileys sin tráfico. El camino vivo
      // (Cloud API) nunca tuvo envío de audio, y en `functions/` no hay ninguno.
      //
      // Mientras estuvo en `true`, la landing prometía «respuestas por voz» y Facturación
      // pintaba «Audio TTS ✓» para algo que no existe. Volver a ponerlo en `true` exige
      // implementar el envío, no sólo el flag.
      audioTTS: false,
      vision: true
    },
    integrationSlots: null,
    audioMinutesIncluded: 500,
    messagesIncluded: 72000,
    imagesIncluded: 1000,
    reposIncludedMB: 1024,
    procesamientoIncludedMB: 2048,
    allowedAddons: [
      'addon_bot',
      'addon_seats',
      'addon_numero',
      'addon_repos',
      'addon_procesamiento',
      'addon_mensajes',
      'addon_ads_studio',
      'addon_calendar'
    ],
    addonLimits: {
      addon_bot: 2,
      addon_seats: null,
      addon_numero: 2,
      addon_repos: null,
      addon_procesamiento: null,
      addon_mensajes: null,
      addon_ads_studio: 1,
      addon_calendar: null
    }
  }
};

// Addon metadata (mirrored from backend)
export const addonMetadata = {
  addon_bot: {
    name: 'Asistente IA adicional',
    description: 'Agrega un asistente IA adicional a tu cuenta',
    icon: '🤖',
    requiresPlan: ['starter', 'pro'],
    status: 'available',
    category: 'bots',
    unitLabel: 'bot',
    pricePerUnit: 47,
    maxPerPlan: { starter: 1, pro: 2 }
  },
  addon_seats: {
    name: 'Usuario adicional',
    description: 'Agrega un miembro más a tu equipo',
    icon: '👤',
    requiresPlan: ['pro'],
    status: 'available',
    category: 'team',
    unitLabel: 'asiento',
    pricePerUnit: 25,
    maxPerPlan: { pro: null }
  },
  addon_numero: {
    name: 'Número WhatsApp extra',
    description: 'Conecta un número WhatsApp adicional',
    icon: '📱',
    requiresPlan: ['pro'],
    status: 'available',
    category: 'channels',
    unitLabel: 'número',
    pricePerUnit: 37,
    maxPerPlan: { pro: 2 }
  },
  addon_calendar: {
    name: 'Agenda adicional',
    description: 'Una agenda más: el calendario de otra persona del equipo',
    icon: '📅',
    requiresPlan: ['starter', 'pro'],
    status: 'available',
    category: 'integrations',
    unitLabel: 'agenda',
    pricePerUnit: 27,
    maxPerPlan: { starter: null, pro: null },
    note: 'Suma una agenda a las de tu plan. En Starter, además, permite usar calendario junto a E-Commerce.'
  },
  addon_ecommerce: {
    name: 'E-Commerce',
    description: 'Conecta Shopify o WooCommerce para ventas automáticas',
    icon: '🛒',
    requiresPlan: ['starter'],
    status: 'available',
    category: 'integrations',
    unitLabel: 'e-commerce',
    pricePerUnit: 37,
    maxPerPlan: { starter: 1 },
    note: 'Starter: desbloquea si ya usas Calendar. Pro: incluido, no se vende'
  },
  addon_repos: {
    name: 'SmartDocs +500 MB',
    description: 'Amplía tu almacenamiento de documentos en 500 MB',
    icon: '📄',
    requiresPlan: ['starter', 'pro'],
    status: 'available',
    category: 'storage',
    unitLabel: '500 MB',
    pricePerUnit: 67,
    maxPerPlan: { starter: null, pro: null }
  },
  addon_mensajes: {
    name: 'Mensajes +12.000/mes',
    description: 'Amplía cuántos mensajes puede responder tu asistente cada mes',
    icon: '💬',
    requiresPlan: ['starter', 'pro'],
    status: 'available',
    category: 'usage',
    unitLabel: '12.000 mensajes/mes',
    pricePerUnit: 39,
    maxPerPlan: { starter: null, pro: null }
  },
  addon_procesamiento: {
    name: 'Procesamiento +500 MB/mes',
    description: 'Amplía cuántos MB de documentos puedes indexar cada mes',
    icon: '⚙️',
    requiresPlan: ['starter', 'pro'],
    status: 'available',
    category: 'storage',
    unitLabel: '500 MB/mes',
    pricePerUnit: 19,
    maxPerPlan: { starter: null, pro: null }
  },
  addon_ads_studio: {
    name: 'Ads Studio',
    description: 'Genera anuncios con IA y publica en Meta, TikTok y Google Ads',
    icon: '📢',
    requiresPlan: ['starter', 'pro'],
    status: 'available',
    category: 'marketing',
    unitLabel: 'Ads Studio',
    pricePerUnit: 97,
    maxPerPlan: { starter: 1, pro: 1 }
  }
};

/**
 * Get plan limits for a specific plan
 */
export function getLimitsForPlan(planId) {
  const normalizedPlan = (planId || 'starter').toLowerCase().trim();
  return planLimits[normalizedPlan] || planLimits.starter;
}

/**
 * Normalize addonsActivos to object format (backward compat)
 */
export function normalizeAddons(addonsActivos) {
  if (!addonsActivos) return {};
  if (Array.isArray(addonsActivos)) {
    const converted = {};
    for (const addon of addonsActivos) {
      converted[addon] = 1;
    }
    return converted;
  }
  return addonsActivos;
}

/**
 * Calculate effective limits including addons
 */
export function calculateEffectiveLimits(baseLimits, addonsActivos = {}) {
  const addons = normalizeAddons(addonsActivos);

  return {
    // `?? Infinity` y no `!== null`: con `undefined`, `Math.min(x, undefined)`
    // es NaN, y un tope NaN deja pasar cualquier comparación.
    botsTotal: Math.min(baseLimits.botsIncluded + (addons.addon_bot || 0), baseLimits.botsMax ?? Infinity),
    seatsTotal: baseLimits.seatsMax !== null
      ? Math.min(baseLimits.seatsIncluded + (addons.addon_seats || 0), baseLimits.seatsMax)
      : baseLimits.seatsIncluded + (addons.addon_seats || 0),
    numerosTotal: Math.min(baseLimits.numerosIncluded + (addons.addon_numero || 0), baseLimits.numerosMax ?? Infinity),
    // Misma aritmética que el servidor (`planLimits.js`): +1 agenda por add-on, apilable.
    agendasTotal: Math.min((baseLimits.agendasIncluded || 0) + (addons.addon_calendar || 0), baseLimits.agendasMax ?? Infinity),
    // `addon_mensajes`: +12.000 al mes por $39, apilable. Antes esto era
    // `messagesIncluded` a secas y el add-on lo sumaba `planValidation.js` con
    // un `* 2000` propio — el tope del Starter viejo, no el tamaño de un add-on.
    messagesTotal: baseLimits.messagesIncluded + ((addons.addon_mensajes || 0) * 12000),
    reposTotalMB: baseLimits.reposIncludedMB + ((addons.addon_repos || 0) * 500),
    // `addon_procesamiento` es MENSUAL, a diferencia de `addon_repos`, que
    // amplía un techo permanente. Faltaba entero en esta copia: la UI no sabía
    // que la cuota de procesamiento existe.
    procesamientoTotalMB: (baseLimits.procesamientoIncludedMB || 0) + ((addons.addon_procesamiento || 0) * 500),
    audioMinutesTotal: baseLimits.audioMinutesIncluded,
    imagesTotal: baseLimits.imagesIncluded
  };
}

/**
 * Check if a feature is included in a plan
 */
export function isPlanFeatureIncluded(planId, feature) {
  const limits = getLimitsForPlan(planId);
  return limits.featuresIncluded[feature] === true;
}

/**
 * Check if a channel is included in a plan
 */
export function isPlanChannelIncluded(planId, channel) {
  const limits = getLimitsForPlan(planId);
  return limits.channelsIncluded[channel] === true;
}

/**
 * Check if client can create a new bot based on current usage
 * @param {object} clientData - Client/org document data
 * @param {number} [actualBotsCount] - Actual count of bot documents (preferred over usage.botsCount)
 */
export function canCreateBotLocally(clientData, actualBotsCount) {
  if (!clientData) return false;

  const limits = clientData.limits || getLimitsForPlan(clientData.planId || clientData.plan);
  const addons = normalizeAddons(clientData.addonsActivos);

  // Prefer actual count from Firestore query over the counter field
  const currentBotsCount = actualBotsCount != null
    ? actualBotsCount
    : (clientData.usage?.botsCount || 0);

  const addonBotQty = addons.addon_bot || 0;
  const totalBotsAllowed = Math.min(limits.botsIncluded + addonBotQty, limits.botsMax);

  return currentBotsCount < totalBotsAllowed;
}

/**
 * Get user-friendly message for why bot creation is blocked
 */
export function getBotCreationBlockedMessage(clientData, actualBotsCount) {
  if (!clientData) return i18n.t('bots:list.clientNotFound');

  const limits = clientData.limits || getLimitsForPlan(clientData.planId || clientData.plan);
  const currentBotsCount = actualBotsCount != null
    ? actualBotsCount
    : (clientData.usage?.botsCount || 0);
  const addons = normalizeAddons(clientData.addonsActivos);

  const addonBotQty = addons.addon_bot || 0;
  const totalBotsAllowed = Math.min(limits.botsIncluded + addonBotQty, limits.botsMax);

  if (currentBotsCount >= totalBotsAllowed) {
    if (currentBotsCount >= limits.botsMax) {
      return i18n.t('bots:list.maxBotsReached');
    }
    return i18n.t('bots:list.botsLimitReached');
  }

  return '';
}

/**
 * Check if an addon is available
 */
export function isAddonAvailable(addonId) {
  const metadata = addonMetadata[addonId];
  return metadata && metadata.status === 'available';
}

/**
 * Check if more of an addon can be purchased for a plan
 */
export function canAddMoreAddon(addonId, planId, currentQty = 0) {
  const metadata = addonMetadata[addonId];
  if (!metadata || !metadata.maxPerPlan) return false;

  const max = metadata.maxPerPlan[planId];
  if (max === null || max === undefined) return true;
  return currentQty < max;
}

/**
 * Get addons available for a specific plan
 */
export function getAddonsForPlan(planId) {
  const normalizedPlan = (planId || 'starter').toLowerCase().trim();
  const addons = [];

  for (const [addonId, metadata] of Object.entries(addonMetadata)) {
    if (metadata.requiresPlan && metadata.requiresPlan.includes(normalizedPlan)) {
      addons.push({
        id: addonId,
        ...metadata
      });
    }
  }

  return addons;
}

/**
 * Calculate usage percentages for display
 */
/**
 * Asientos usados y totales. Prefiere `seats` —lo que el servidor mantiene y enforza— y sólo
 * cae a `usage.seatsCount` cuando no existe (clientes individuales y documentos antiguos).
 *
 * `used` se DERIVA como `total − available` en vez de leer un campo propio: así no puede
 * contradecir a `available`, que es el número con el que el servidor decide si cabe otra
 * invitación. Nunca baja de 0: un `available` mayor que `total` daría un negativo, y ya
 * tenemos el caso de `numerosCount` en −3 por restar sin suelo.
 */
/**
 * Cómo se pinta un contador que puede tener decimales.
 *
 * MEDIDO EN PRODUCCIÓN el 8-oct-2026, con tres tarjetas mal a la vez:
 *  · `audioMinutesThisMonth` valía 0,1285 —una nota de voz de 7,71 s, la primera que el
 *    contador llegó a registrar— y Facturación mostraba «0 / 500», porque la tarjeta la
 *    formateaba con `toFixed(0)`. Un contador que enseña 0 cuando ha subido no informa: parece
 *    que la medición no funciona, y fue justo lo que se reportó.
 *  · `repoStorageMB` valía 5.999969482421875 en una org y 0.01598644256591797 en dos más, y las
 *    tarjetas de MB no pasaban formato ninguno: pintaban el flotante con sus quince decimales.
 *
 * Los dos extremos del mismo error: redondear a entero una magnitud que vive por debajo de 1, y
 * no redondear nada. La regla aquí: NUNCA mostrar 0 para algo que no es 0, y nunca más de dos
 * decimales. Por debajo de un minuto se cambia de unidad a segundos, que es como lo diría una
 * persona —«8 s», no «0,1»—; el total sigue en minutos y por eso lleva su unidad al lado.
 *
 * Es pura y recibe el idioma: el separador decimal de es/pt es la coma y el de en el punto, y
 * eso lo decide `toLocaleString`, no nosotros.
 */
export function formatearUso(valor, unidad, idioma) {
  if (!Number.isFinite(valor) || valor < 0) return String(valor === 0 ? 0 : valor);
  if (Number.isInteger(valor)) return valor.toLocaleString(idioma);
  // Menos de un minuto: en segundos, y nunca «0 s» para un audio que existió.
  if (unidad === 'min' && valor < 1) return `${Math.max(1, Math.round(valor * 60))} s`;
  const decimales = valor >= 10 ? 0 : (valor < 0.1 ? 2 : 1);
  return valor.toLocaleString(idioma, { maximumFractionDigits: decimales });
}

/**
 * La tarjeta de números a partir del conteo real de documentos.
 *
 * `null` cuando todavía no se ha contado: `UsageCard` no pinta nada con un `usage` nulo, y eso
 * es lo que queremos. La alternativa —mostrar 0 mientras se cuenta— es la misma trampa que
 * tenía el contador roto: un cero que parece un dato.
 */
export function numerosDe(numerosReales, effective) {
  if (!Number.isFinite(numerosReales) || numerosReales < 0) return null;
  const total = effective.numerosTotal;
  return {
    used: numerosReales,
    total,
    percentage: total > 0 ? Math.round((numerosReales / total) * 100) : 0
  };
}

export function seatsDe(clientData, effective) {
  const s = clientData && clientData.seats;
  const usage = (clientData && clientData.usage) || {};
  if (s && typeof s.total === 'number' && typeof s.available === 'number') {
    const total = s.total;
    const used = Math.max(0, total - s.available);
    return { used, total, percentage: total > 0 ? Math.round((used / total) * 100) : 0 };
  }
  const used = usage.seatsCount || 0;
  const total = effective.seatsTotal;
  return { used, total, percentage: total > 0 ? Math.round((used / total) * 100) : 0 };
}

export function calculateUsagePercentages(clientData, numerosReales) {
  if (!clientData) return {};

  const limits = clientData.limits || getLimitsForPlan(clientData.planId || clientData.plan);
  const usage = clientData.usage || {};
  const addons = normalizeAddons(clientData.addonsActivos);
  const effective = calculateEffectiveLimits(limits, addons);

  return {
    bots: {
      used: usage.botsCount || 0,
      total: effective.botsTotal,
      percentage: Math.round(((usage.botsCount || 0) / effective.botsTotal) * 100)
    },
    // ASIENTOS: la contabilidad viva es `organization.seats`, no `usage.seatsCount`
    // (8-oct-2026). `seatsCount` NO LO ESCRIBE NADIE —cero escritores en el repo, y vale
    // `undefined` en las cinco organizaciones—, así que la tarjeta decía «0 / N» siempre.
    // `seats.total`/`seats.available` sí los mantienen el webhook de Stripe
    // (`stripeWebhook.js:149`) y las altas y bajas de miembros
    // (`server/src/routes/organizationRoutes.js:722`, `:1124`), y es lo que el servidor
    // ENFORZA al invitar. Medido: `available = total − activos` cuadra en las cinco.
    //
    // El cliente individual (`clientes/{uid}`) no tiene `seats`; ahí se cae al camino viejo.
    seats: seatsDe(clientData, effective),
    // NÚMEROS · la cantidad REAL de documentos, contada por quien la pinta.
    //
    // `usage.numerosCount` ya NO se lee aquí (8-oct-2026). Valía **−2** en la organización de
    // PUX con un número conectado, y el autoajuste del navegador que debía corregirlo estaba
    // en una puerta que no se abría. El resto del sistema nunca lo creyó: los topes de verdad
    // —`createNumero.js:94`, `whatsappBusinessCallback.js:318`, `planChangeHelpers.js`— cuentan
    // DOCUMENTOS, y este era el último lector que quedaba.
    //
    // Sin conteo no se inventa un 0: devuelve `null` y la tarjeta no se pinta. Un hueco se
    // nota y se pregunta; un 0 falso se cree, y es justo lo que pasó con los minutos de audio.
    numeros: numerosDe(numerosReales, effective),
    messages: {
      used: usage.messagesThisMonth || 0,
      total: effective.messagesTotal,
      percentage: Math.round(((usage.messagesThisMonth || 0) / effective.messagesTotal) * 100)
    },
    audioMinutes: {
      used: usage.audioMinutesThisMonth || 0,
      total: effective.audioMinutesTotal,
      percentage: Math.round(((usage.audioMinutesThisMonth || 0) / effective.audioMinutesTotal) * 100)
    },
    images: {
      used: usage.imagesThisMonth || 0,
      total: effective.imagesTotal,
      percentage: Math.round(((usage.imagesThisMonth || 0) / effective.imagesTotal) * 100)
    },
    // `repoStorageMB` y NO `reposUsedMB` (8-oct-2026). El lector apuntaba a un campo que
    // NO ESCRIBE NADIE en todo el repo: el escritor es `uploadKnowledgeDocument.js:253`
    // (`usage.repoStorageMB`, +MB) y el descuento `bot/utils/vectorStore.js:546` (−MB).
    // Resultado medido: la tarjeta de SmartDocs marcaba 0 MB siempre, con una organización
    // que tenía 6 MB almacenados. Un contador que no puede subir nunca no avisa de nada.
    repos: {
      used: usage.repoStorageMB || 0,
      total: effective.reposTotalMB,
      percentage: effective.reposTotalMB > 0
        ? Math.round(((usage.repoStorageMB || 0) / effective.reposTotalMB) * 100)
        : 0
    },
    // Procesamiento mensual. Faltaba, y es una cuota que CORTA desde F2:
    // `uploadKnowledgeDocument` rechaza con `resource-exhausted`. El cliente
    // veía el almacenamiento —que también corta— y no esto.
    //
    // Es de PERIODO, a diferencia de `repos`: se reinicia con el ciclo, por eso
    // el campo se llama `ThisMonth`.
    procesamiento: {
      used: usage.procesamientoMBThisMonth || 0,
      total: effective.procesamientoTotalMB,
      percentage: effective.procesamientoTotalMB > 0
        ? Math.round(((usage.procesamientoMBThisMonth || 0) / effective.procesamientoTotalMB) * 100)
        : 0
    }
  };
}

/**
 * Format plan name for display
 */
export function formatPlanName(planId) {
  const key = planId?.toLowerCase();
  if (key === 'starter') return i18n.t('billing:plan.starter');
  if (key === 'pro') return i18n.t('billing:plan.pro');
  return i18n.t('billing:plan.noPlan');
}

/**
 * Get plan color for UI
 */
export function getPlanColor(planId) {
  const colors = {
    starter: '#3b82f6',
    pro: '#8b5cf6'
  };
  return colors[planId?.toLowerCase()] || '#6b7280';
}

/**
 * Check if client has active addon
 */
export function hasAddon(clientData, addonId) {
  if (!clientData) return false;
  const addons = normalizeAddons(clientData.addonsActivos);
  return (addons[addonId] || 0) > 0;
}

/**
 * Check if feature/channel is unlocked (either included or via addon)
 */
export function isFeatureUnlocked(clientData, feature) {
  if (!clientData) return false;

  const planId = clientData.planId || clientData.plan;
  const limits = clientData.limits || getLimitsForPlan(planId);
  const plan = (planId || 'starter').toLowerCase();

  // Check if included in plan
  if (limits.featuresIncluded[feature] === true) {
    // Starter calendar/ecommerce exclusivity check
    if (plan === 'starter' && (feature === 'calendar' || feature === 'ecommerce')) {
      return _isStarterIntegrationUnlocked(clientData, feature);
    }
    return true;
  }

  // Check if enabled via addon
  const addons = normalizeAddons(clientData.addonsActivos);
  const featureAddonMap = {
    calendar: 'addon_calendar',
    ecommerce: 'addon_ecommerce',
    repositorios: 'addon_repos'
  };

  const requiredAddon = featureAddonMap[feature];
  return requiredAddon && (addons[requiredAddon] || 0) > 0;
}

/**
 * Check Starter integration slot exclusivity
 */
function _isStarterIntegrationUnlocked(clientData, feature) {
  const addons = normalizeAddons(clientData.addonsActivos);
  const thisAddon = feature === 'calendar' ? 'addon_calendar' : 'addon_ecommerce';

  // If has addon for this feature, always unlocked
  if ((addons[thisAddon] || 0) > 0) return true;

  // Check if the other integration is using the free slot
  const otherFeature = feature === 'calendar' ? 'ecommerce' : 'calendar';
  const integrations = clientData.activeIntegrations || {};

  // If the other is NOT active, this one can use the free slot
  return integrations[otherFeature] !== true;
}

/**
 * Get suggested action when feature is locked
 */
export function getSuggestedAction(clientData, feature) {
  if (!clientData) return null;

  const planId = (clientData.planId || clientData.plan || '').toLowerCase();
  const limits = getLimitsForPlan(planId);

  const featureAddonMap = {
    calendar: 'addon_calendar',
    ecommerce: 'addon_ecommerce',
    repositorios: 'addon_repos'
  };

  // Starter calendar/ecommerce exclusivity
  if (planId === 'starter' && (feature === 'calendar' || feature === 'ecommerce')) {
    const requiredAddon = featureAddonMap[feature];
    const metadata = addonMetadata[requiredAddon];
    return {
      type: 'add_addon',
      message: i18n.t('addons:suggestedAction.starterIntegration', { name: metadata.name }),
      action: i18n.t('addons:suggestedAction.addAddon'),
      addonId: requiredAddon
    };
  }

  // Channel not included (e.g., email for Starter)
  if (feature === 'email' && planId === 'starter') {
    return {
      type: 'upgrade_plan',
      message: i18n.t('addons:suggestedAction.emailProOnly'),
      action: i18n.t('addons:suggestedAction.upgradeToPro')
    };
  }

  // Generic addon suggestion
  const requiredAddon = featureAddonMap[feature];
  if (requiredAddon && limits.allowedAddons.includes(requiredAddon)) {
    const metadata = addonMetadata[requiredAddon];
    return {
      type: 'add_addon',
      message: i18n.t('addons:suggestedAction.requiresAddon', { name: metadata.name }),
      action: i18n.t('addons:suggestedAction.addAddon'),
      addonId: requiredAddon
    };
  }

  return {
    type: 'upgrade_plan',
    message: i18n.t('addons:suggestedAction.notAvailable'),
    action: i18n.t('addons:suggestedAction.viewPlans')
  };
}
